import {
  AffiliationsType,
  AssignmentNotificationMode,
  SpecialistGroupAffiliationType,
  SpecialistGroupParticipantType,
  SpecialistGroupType,
} from '@dermloop/static-values';
import { IsLessThanOrEqualTo } from '@dermloop/utils';
import { Exclude } from 'class-transformer';
import {
  ArrayMaxSize,
  ArrayMinSize,
  IsArray,
  IsBoolean,
  IsEnum,
  IsInt,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  Min,
} from 'class-validator';
import { BaseCreateDto, BaseDto, BaseUpdateDto } from './base.dto';
import type { ConditionTypeDto } from './condition-type.dtos';
import { FederationDto, MAX_REASSIGNMENT_HOURS } from './federation.dtos';
import { MedicalUnitDto } from './medical-unit.dtos';
import type { UserAffiliationDto } from './user-affiliation.dtos';

export interface SpecialistGroupDto extends BaseDto {
  name: string;
  type: SpecialistGroupType;
  federation: FederationDto;
  federationId: string;
  description?: string;
  holidayListId?: string | null;
  assignmentNotificationMode: AssignmentNotificationMode;
}

/**
 * The affiliation columns a specialist-group member row exposes. The affiliation row also
 * carries invitation secrets, which never leave the API.
 */
export type SpecialistGroupMemberAffiliationDto = Pick<
  UserAffiliationDto,
  'id' | 'userId' | 'medicalUnitId' | 'user'
>;

export interface SpecialistGroupMemberDto extends BaseDto {
  medicalUnit?: MedicalUnitDto;
  medicalUnitId?: string;
  affiliation?: SpecialistGroupMemberAffiliationDto;
  affiliationId?: string | null;
  conditionTypes?: ConditionTypeDto[];
  specialistGroupId: string;
  specialistGroup: SpecialistGroupDto;
  ordering: number;
  lastAssignedAt?: Date | null;
  reassignmentHours?: number | null;
  preReassignmentNotifyHours?: number | null;
}

export class SearchSpecialistGroupDto {
  /**
   * Empty string corresponds to no search query provided.
   * Searches for specialist group name or federation name.
   * */
  @IsOptional()
  @IsString()
  searchQuery?: string;

  /** Filter to specific federation */
  @IsOptional()
  @IsUUID()
  federationId?: string;

  @IsOptional()
  @IsNumber()
  @Min(0)
  skip?: number;

  @IsOptional()
  @IsNumber()
  @Min(1)
  @Max(20)
  take?: number;
}
export class CreateSpecialistGroupDto
  extends BaseCreateDto
  implements Partial<SpecialistGroupDto>
{
  @IsNotEmpty()
  @IsString()
  name: string;

  @Exclude()
  type?: never; // Currently not supported, as only one type exists

  @Exclude()
  federation?: never;

  @IsOptional()
  @IsUUID()
  federationId?: string;

  @IsOptional()
  @IsString()
  @IsNotEmpty()
  description?: string;

  @IsOptional()
  @IsUUID()
  holidayListId?: string | null;

  @IsOptional()
  @IsEnum(AssignmentNotificationMode)
  assignmentNotificationMode?: AssignmentNotificationMode;
}

export class UpdateSpecialistGroupDto
  extends BaseUpdateDto
  implements Partial<SpecialistGroupDto>
{
  @IsNotEmpty()
  @IsString()
  name: string;

  @Exclude()
  type?: never; // Currently not supported, as only one type exists

  @Exclude()
  federation?: never;

  @Exclude()
  federationId?: never;

  @IsOptional()
  @IsString()
  @IsNotEmpty()
  description?: string;

  @IsOptional()
  @IsUUID()
  holidayListId?: string | null;

  @IsOptional()
  @IsEnum(AssignmentNotificationMode)
  assignmentNotificationMode?: AssignmentNotificationMode;
}

/**
 * DTO for searching for specialists within the federation of a specific specialist group.
 * Returns either users who could be added to the group
 * or users who are already part of the group, depending on the `excludeExistingMembers` flag.
 */
export class SearchSpecialistGroupUsersDto {
  /**
   * Empty string corresponds to no search query provided.
   * Searches for user email and medical unit name.
   */
  @IsOptional()
  @IsNotEmpty()
  @IsString()
  searchQuery?: string;

  @IsUUID()
  specialistGroupId: string;

  /**
   * If true, returns users not yet in the group (eligible to be added).
   * If false or omitted, returns users already in the group.
   */
  @IsOptional()
  @IsBoolean()
  excludeExistingMembers?: boolean;

  @IsOptional()
  @IsBoolean()
  onVacation?: boolean;
}

export interface SearchSpecialistGroupUserResultDto {
  /** Set for a user-type result. Pass as an entry in
   * CreateSpecialistGroupMembersDto.affiliationIds. */
  affiliationId?: string;
  userId?: string;
  userEmail?: string;
  userFirstName?: string;
  userLastName?: string;
  medicalUnitId?: string;
  medicalUnitName?: string;
  medicalUnitSharedEmail?: string;
  /** If the user is already a member of the specialist group and `excludeExistingMembers = false`, an id will be set */
  specialistGroupMemberId?: string;
  /** Set if the user is already a member of the specialist group and `excludeExistingMembers = false` */
  ordering?: number;
  /** The condition types in effect for this affiliation/medical unit, for display in the picker. */
  conditionTypes?: ConditionTypeDto[];
}

export interface SpecialistGroupMedicalUnitDto {
  medicalUnitId: string;
  medicalUnitName: string;
  /** If true, the specialist group is a preferred tele specialist for medical unit */
  preferredTeleSpecialists?: boolean;
  /** If true, the specialist group is an allowed tele specialist for medical unit */
  allowedTeleSpecialists?: boolean;
}

export interface SpecialistGroupJurisdictionalGroupDto {
  jurisdictionalGroupId: string;
  jurisdictionalGroupName: string;
  /** If true, the specialist group is a default preferred tele specialist for jurisdictional group */
  defaultPreferredTeleSpecialists?: boolean;
  /** If true, the specialist group is default allowed tele specialist for jurisdictional group */
  defaultAllowedTeleSpecialists?: boolean;
}

export class SearchSpecialistGroupMembersDto {
  @IsOptional()
  @IsNotEmpty()
  @IsString()
  searchQuery?: string;

  @IsUUID()
  specialistGroupId: string;

  @IsOptional()
  @IsEnum(SpecialistGroupParticipantType)
  type?: SpecialistGroupParticipantType;
}

export class CreateSpecialistGroupMembersDto extends BaseCreateDto {
  @IsUUID()
  specialistGroupId: string;

  @IsOptional()
  @IsUUID('all', { each: true })
  @ArrayMaxSize(100)
  userIds?: string[];

  /** Affiliation ids to add — the preferred way to add a user member going forward, since it
   * names exactly which of the user's affiliations to link instead of leaving that to be
   * resolved. Takes precedence over `userIds` for the same underlying person. */
  @IsOptional()
  @IsUUID('all', { each: true })
  @ArrayMaxSize(100)
  affiliationIds?: string[];

  @IsOptional()
  @IsUUID('all', { each: true })
  @ArrayMaxSize(100)
  medicalUnitIds?: string[];

  // Omitted or empty gives every new member their own full grant, which is what an
  // untouched picker means — the selection applies to the whole batch.
  @IsOptional()
  @IsArray()
  @ArrayMaxSize(1000)
  @IsUUID('all', { each: true })
  conditionTypeIds?: string[];

  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(MAX_REASSIGNMENT_HOURS)
  reassignmentHours?: number | null;

  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(MAX_REASSIGNMENT_HOURS)
  @IsLessThanOrEqualTo<CreateSpecialistGroupMembersDto>('reassignmentHours', {
    relatedValueNullable: true,
  })
  preReassignmentNotifyHours?: number | null;
}

export class UpdateSpecialistGroupMemberConditionTypesDto {
  @IsArray()
  @ArrayMinSize(1, { message: 'At least one condition type must be selected' })
  @ArrayMaxSize(1000)
  @IsUUID('all', { each: true })
  conditionTypeIds: string[];
}

export class SearchSelectableConditionTypesDto {
  @IsUUID()
  specialistGroupId: string;

  @IsOptional()
  @IsArray()
  @ArrayMaxSize(1000)
  @IsUUID('all', { each: true })
  affiliationIds?: string[];

  @IsOptional()
  @IsArray()
  @ArrayMaxSize(1000)
  @IsUUID('all', { each: true })
  userIds?: string[];

  // Each id costs a grant lookup, so the cap is what keeps one request from fanning out
  // into thousands of queries.
  @IsOptional()
  @IsArray()
  @ArrayMaxSize(1000)
  @IsUUID('all', { each: true })
  medicalUnitIds?: string[];
}

export interface SpecialistGroupMemberConditionTypesDto {
  selected: ConditionTypeDto[];
  selectable: ConditionTypeDto[];
}

export class UpdateSpecialistGroupMemberDto
  extends BaseUpdateDto
  implements Partial<SpecialistGroupMemberDto>
{
  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(MAX_REASSIGNMENT_HOURS)
  reassignmentHours?: number | null;

  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(MAX_REASSIGNMENT_HOURS)
  @IsLessThanOrEqualTo<UpdateSpecialistGroupMemberDto>('reassignmentHours', {
    relatedValueNullable: true,
  })
  preReassignmentNotifyHours?: number | null;
}

export class SearchSpecialistGroupAffiliationsDto {
  @IsOptional()
  @IsNotEmpty()
  @IsString()
  affiliationSearchQuery?: string;

  @IsUUID()
  specialistGroupId: string;

  @IsOptional()
  @IsEnum(SpecialistGroupAffiliationType)
  affiliationType?: SpecialistGroupAffiliationType;

  @IsOptional()
  @IsEnum(AffiliationsType)
  affiliation?: AffiliationsType;

  @IsOptional()
  @IsNumber()
  @Min(0)
  skip?: number;

  @IsOptional()
  @IsNumber()
  @Min(1)
  @Max(100)
  take?: number;
}
